import { useQuery } from 'react-apollo';
import { isRootNode } from '../../../../common/components/dataViews/DynamicTree';
import { BusinessEntity } from '../../../../model/OrganisationTypes';
import gql from './timetableStructureTree.graphql';
export default function useTimetableStructureTree({ id, leaves = [], query = gql, }) {
    const { loading, error, data: { timetableStructureTree = [] } = {}, } = useQuery(query, {
        variables: {
            id,
            leaves,
        },
    });
    return {
        timetableStructureTree,
        loading,
        error,
    };
}
export function resolveNodeId(node) {
    if (node.__typename === 'Organisation') {
        return `${node.__typename}-${node.id}`;
    }
    return `${node.__typename}-${node.organisationId}-${node.id}`;
}
export function resolveNodeParentId(leaves, node) {
    switch (true) {
        case isOrganisationStructure(node) &&
            !!node.organisationId:
            return `${ORGANISATION}-${node.organisationId}`;
        case isOrganisation(node) && !!node.parentId:
            return `${ORGANISATION}-${node.parentId}`;
        case isOrganisation(node) && !node.parentId:
            return null;
        case isOrgStructureLevel(node) && !!node.parentId:
            return `${ORG_STRUCTURE_LEVEL}-${node.organisationId}-${node.parentId}`;
        case isOrgStructureLevel(node) &&
            !!node.organisationStructureId:
            return `${ORGANISATION_STRUCTURE}-${node.organisationId}-${node.organisationStructureId}`;
        case isProgram(node):
            return `${ORG_STRUCTURE_LEVEL}-${node.organisationId}-${node.programId}`;
        case isSubject(node):
            if (leaves.includes('SUBJECT_SYNTHETIC_PARENT')) {
                return `${SUBJECTS_SYNTHETIC_PARENT}-${node.organisationId}-${node.intakeProgramId}`;
            }
            return `${PROGRAM}-${node.organisationId}-${node.intakeProgramId}`;
        case isClass(node):
            return `${SUBJECT}-${node.organisationId}-${node.intakeProgramSubjectId}`;
        case isStudentGroupSyntheticParent(node):
        case isSubjectsSyntheticParent(node):
            return `${PROGRAM}-${node
                .organisationId}-${node.id}`;
        case isStudentGroup(node):
            if (leaves.includes('STUDENT_GROUP_SYNTHETIC_PARENT')) {
                return `${STUDENT_GROUP_SYNTHETIC_PARENT}-${node.organisationId}-${node.intakeProgramId}`;
            }
            return `${PROGRAM}-${node.organisationId}-${node.intakeProgramId}`;
        default:
            return null;
    }
}
export function resolveNodeTitle(t, resolveProgramTypeSynonym, node) {
    var _a;
    switch (true) {
        case isProgram(node) && !!node.studentsCount:
            return `${node.programName} (${node.studentsCount})`;
        case isProgram(node):
            return node.programName;
        case isStudentGroupSyntheticParent(node):
            return t('#{participant} #{group}', {
                participant: resolveProgramTypeSynonym(node.programGroupTypeId, 'participant'),
                group: resolveProgramTypeSynonym(node.programGroupTypeId, 'participantGroupNamePlural'),
            });
        case isSubjectsSyntheticParent(node):
            return t(resolveProgramTypeSynonym(node.programGroupTypeId, 'activityNamePlural'));
        case isSubject(node) && !!node.studentsCount:
            return `${node.subjectName} (${node.studentsCount})`;
        case isSubject(node):
            return node.subjectName;
        case isClass(node) && !!node.studentsCount:
            return `${node.name} (${node.studentsCount})`;
        case isStudentGroup(node) && !!node.studentsCount:
            return `${node.name} (${node.studentsCount})`;
        default:
            return (_a = node) === null || _a === void 0 ? void 0 : _a.name;
    }
}
const CLASS = 'Class';
const STUDENT_GROUP = 'StudentGroup';
const SUBJECT = 'IntakeProgramXSubject';
const PROGRAM = 'IntakeXProgram';
const SUBJECTS_SYNTHETIC_PARENT = 'SubjectsSyntheticParent';
const STUDENT_GROUP_SYNTHETIC_PARENT = 'StudentGroupSyntheticParent';
const ORG_STRUCTURE_LEVEL = 'OrgStructureLevel';
const ORGANISATION = 'Organisation';
const ORGANISATION_STRUCTURE = 'OrganisationStructure';
export const isClass = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === CLASS;
export const isStudentGroup = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === STUDENT_GROUP;
export const isSubject = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === SUBJECT;
export const isProgram = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === PROGRAM;
export const isSubjectsSyntheticParent = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === SUBJECTS_SYNTHETIC_PARENT;
export const isStudentGroupSyntheticParent = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === STUDENT_GROUP_SYNTHETIC_PARENT;
export const isOrgStructureLevel = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === ORG_STRUCTURE_LEVEL;
export const isOrganisation = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === ORGANISATION;
export const isOrganisationStructure = (node) => (node === null || node === void 0 ? void 0 : node.__typename) === ORGANISATION_STRUCTURE;
const isBusinessEntity = (node) => node.organisationType === BusinessEntity.value;
export const resolveNodeClassName = node => {
    if (isBusinessEntity(node) || isRootNode(node)) {
        return 'jstree-node-bold';
    }
    return '';
};
